<template>
  <div class="customer-history">
    <div v-if="!customerPhone && !customerName" class="text-center p-5">
      <p class="text-muted">
        Khách hàng chưa có số điện thoại hoặc tên để tra cứu phiếu thu cũ.
      </p>
    </div>

    <template v-else>
      <!-- Date Filters -->
      <b-row class="mb-4 align-items-end">
        <b-col md="3" sm="5">
          <label class="small text-muted font-weight-bold">Từ ngày</label>
          <date-picker
            placeholder="Chọn ngày bắt đầu"
            class="form-control form-control-sm"
            :config="datePickerConfig"
            v-model="filters.fromDate"
          ></date-picker>
        </b-col>
        <b-col md="3" sm="5">
          <label class="small text-muted font-weight-bold">Đến ngày</label>
          <date-picker
            placeholder="Chọn ngày kết thúc"
            class="form-control form-control-sm"
            :config="datePickerConfig"
            v-model="filters.toDate"
          ></date-picker>
        </b-col>
        <b-col md="3" sm="2">
          <b-button
            variant="primary"
            size="sm"
            @click="onApplyFilter"
            class="font-weight-bolder"
          >
            <i class="fas fa-search mr-1"></i> Lọc
          </b-button>
          <b-button
            variant="secondary"
            size="sm"
            @click="onResetFilter"
            class="ml-2"
          >
            Làm mới
          </b-button>
        </b-col>
      </b-row>

      <trade-in-list-table
        :items="items"
        :is-loading="isLoading"
        :total-row="totalItems"
        :number-of-page="totalPages"
        :current-page="Number(currentPage)"
        :pagination-link-params="{ id: customerId }"
        @page-change="onPageChange"
      />
    </template>
  </div>
</template>

<script>
import { defineComponent, onMounted, ref } from 'vue';
import ApiService from '@/core/services/api.service';
import TradeInListTable from '@/view/pages/trade-in/components/TradeInListTable.vue';
import datePicker from 'vue-bootstrap-datetimepicker';
import moment from 'moment';
import { convertToDateSlash } from '@/utils/date';
import useCustomerHistory from '../composables/useCustomerHistory';

export default defineComponent({
  name: 'CustomerTradeInHistoryTab',

  components: {
    TradeInListTable,
    datePicker,
  },

  props: {
    customerId: {
      type: [String, Number],
      required: true,
    },
    customerPhone: {
      type: String,
      default: '',
    },
    customerName: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const filters = ref({
      fromDate: null,
      toDate: null,
    });

    const datePickerConfig = {
      format: 'DD/MM/YYYY',
      useCurrent: false,
      showClear: true,
      showClose: true,
      locale: 'vi',
    };

    // Map dữ liệu giống trang danh sách thu cũ để TradeInListTable render đúng
    const mapTradeInItem = (element) => ({
      id: element.id,
      customerId: element.customerId,
      customerName: element.customerName,
      customerPhone: element.customerPhoneNo,
      productName: element.productName,
      productCode: element.productCode,
      productBarCode: element.productBarCode,
      productImageUrl: element.productImageUrl,
      imeiCode: element.productIMEI,
      estimatePrice: element.estimationBuyingPrice,
      finalPrice: element.finalBuyingPrice,
      differencePrice:
        element.estimationBuyingPrice - element.finalBuyingPrice,
      date: convertToDateSlash(element.createdAt),
      storeName: element.storeName,
      status: element.status,
      createdBy: element.createdBy,
      statusName: element.statusName,
      tradeInType: element.tradeInType,
      tradeInTypeName: element.tradeInTypeName,
      stockSlipCode: element.stockSlipCode,
      stockSlipId: element.stockSlipId,
      note: element.note,
      hasUploadImage: element.hasUploadImage,
      billNumber: element.billNumber || '',
    });

    const loadTradeIns = async (page) => {
      const params = {
        page,
        limit: 10,
        customerId: Number(props.customerId),
      };

      if (filters.value.fromDate) {
        params.fromDate = moment(filters.value.fromDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      }
      if (filters.value.toDate) {
        params.toDate = moment(filters.value.toDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      }

      const response = await ApiService.query('/v1/trade-ins', { params });
      const data = response.data.data || {};
      return {
        items: (data.data || []).map(mapTradeInItem),
        totalRow: data.totalRow || 0,
        totalPage: data.totalPage || 0,
      };
    };

    const {
      items,
      totalItems,
      totalPages,
      currentPage,
      isLoading,
      fetchPage,
      onPageChange,
    } = useCustomerHistory(loadTradeIns);

    const onApplyFilter = () => {
      fetchPage(1);
    };

    const onResetFilter = () => {
      filters.value.fromDate = null;
      filters.value.toDate = null;
      fetchPage(1);
    };

    onMounted(() => {
      if (props.customerPhone || props.customerName) {
        fetchPage(1);
      }
    });

    return {
      items,
      totalItems,
      totalPages,
      currentPage,
      isLoading,
      onPageChange,
      filters,
      datePickerConfig,
      onApplyFilter,
      onResetFilter,
    };
  },
});
</script>

<style scoped>
.customer-history {
  padding: 1rem 0;
}
</style>
